import { ColorConstants } from "../../../../constants/styles/ColorConstants";
import { GanttPhaseKey } from "../models";

// ─── Phase colors (aligns with ProjectRoadmapColourMap) ──────────────────────

export const PHASE_COLORS: Record<GanttPhaseKey, { bar: string; bg: string; border: string }> = {
  feasibility: {
    bar: "var(--color-indigo-500)",
    bg: "var(--color-indigo-50)",
    border: "var(--color-indigo-200)",
  },
  design: {
    bar: "var(--color-coral-500)",
    bg: "var(--color-coral-50)",
    border: "var(--color-coral-200)",
  },
  implementation: {
    bar: "var(--color-aqua-500)",
    bg: "var(--color-aqua-50)",
    border: "var(--color-aqua-200)",
  },
  ongoingMonitoring: {
    bar: "var(--color-green-500)",
    bg: "var(--color-green-50)",
    border: "var(--color-green-200)",
  },
};

export const getPhaseColor = (phase: GanttPhaseKey | undefined): { bar: string; bg: string; border: string } =>
  (phase && PHASE_COLORS[phase]) || {
    bar: "var(--color-grey-400)",
    bg: "var(--color-grey-25)",
    border: "var(--color-grey-200)",
  };

// ─── Stage colours (ordered pipeline scale — see Figma stage spec) ───────────

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Ordered, positional stage scale. Fill = token at 20% opacity, border = the solid token. */
const STAGE_SCALE: { token: string; hex: string }[] = [
  { token: "violet-500", hex: ColorConstants.VIOLET_500 },
  { token: "sky-blue-500", hex: ColorConstants.SKY_BLUE_500 },
  { token: "amber-orange-500", hex: ColorConstants.AMBER_ORANGE_500 },
  { token: "aqua-500", hex: ColorConstants.AQUA_500 },
  { token: "goldenrod-500", hex: ColorConstants.GOLDENROD_500 },
  { token: "blue-500", hex: ColorConstants.BLUE_500 },
  { token: "magenta-500", hex: ColorConstants.MAGENTA_500 },
  { token: "goldenrod-600", hex: ColorConstants.GOLDENROD_600 },
  { token: "sky-blue-600", hex: ColorConstants.SKY_BLUE_600 },
];

/** The final stage always uses the success colour. */
const STAGE_FINAL = { token: "mint-green-500", hex: ColorConstants.MINT_GREEN_500 };

const STAGE_FILL_OPACITY = 0.2;

/**
 * Returns { bar, bg, border } for a stage at `index` within `total` ordered stages.
 * Colour is positional (cycling the scale beyond 9), and the last stage is always
 * the success colour. `bg` is the solid colour at 20% opacity per the design spec.
 */
export const getStageColor = (index: number, total: number): { bar: string; bg: string; border: string } => {
  const isFinal = index === total - 1;
  const entry = isFinal ? STAGE_FINAL : STAGE_SCALE[index % STAGE_SCALE.length];
  const solid = `var(--color-${entry.token})`;
  return { bar: solid, border: solid, bg: hexToRgba(entry.hex, STAGE_FILL_OPACITY) };
};

// ─── Task status ──────────────────────────────────────────────────────────────

/** Default status for a task that hasn't been given one. */
export const UNASSIGNED_STATUS = "Unassigned";

/** Resolves a (possibly missing/blank) task status to its display value. */
export const resolveTaskStatus = (status?: string): string => (status && status.trim() ? status : UNASSIGNED_STATUS);

// ─── Task status colors ───────────────────────────────────────────────────────

const TASK_STATUS_COLORS: Record<string, { dot: string; bg: string; text: string }> = {
  // "Complete"/"To do" are the widget's own vocabulary (roadmap phases, widget stories); the tasks
  // variant feeds the Kanban vocabulary ("Completed"/"Not started"). Both are kept so either
  // consumer resolves to the same colours.
  Complete: {
    dot: "var(--color-green-500)",
    bg: "var(--color-green-50)",
    text: "var(--color-green-600)",
  },
  Completed: {
    dot: "var(--color-green-500)",
    bg: "var(--color-green-50)",
    text: "var(--color-green-600)",
  },
  "In progress": {
    dot: "var(--color-blue-500)",
    bg: "var(--color-blue-50)",
    text: "var(--color-blue-600)",
  },
  "In review": {
    dot: "var(--color-amber-orange-500)",
    bg: "var(--color-amber-orange-50)",
    text: "var(--color-amber-orange-600)",
  },
  Blocked: {
    dot: "var(--color-red-500)",
    bg: "var(--color-red-50)",
    text: "var(--color-red-600)",
  },
  "To do": {
    dot: "var(--color-grey-400)",
    bg: "var(--color-grey-50)",
    text: "var(--color-grey-500)",
  },
  "Not started": {
    dot: "var(--color-grey-400)",
    bg: "var(--color-grey-50)",
    text: "var(--color-grey-500)",
  },
  Unassigned: {
    dot: "var(--color-grey-300)",
    bg: "var(--color-grey-25)",
    text: "var(--color-grey-400)",
  },
};

/** Returns { dot, bg, text } CSS var strings for a task status label */
export const getTaskStatusColor = (status: string): { dot: string; bg: string; text: string } =>
  TASK_STATUS_COLORS[status] ?? TASK_STATUS_COLORS.Unassigned;

// ─── Deadline urgency color (for bar/milestone fill) ─────────────────────────

/**
 * Returns a { bg, border } colour-token pair for a task deadline bar or milestone.
 *
 * | Condition                     | bg                  | border               |
 * |-------------------------------|---------------------|----------------------|
 * | Complete or no deadline       | grey-25             | grey-200             |
 * | Overdue or ≤ 7 days remaining | red-50              | red-200              |
 * | 7–30 days remaining           | amber-orange-25     | amber-orange-200     |
 * | > 30 days remaining           | mint-green-100      | mint-green-300       |
 */
export const getDeadlineBarColors = (deadline: Date | undefined, status: string): { bg: string; border: string } => {
  if (status === "Complete" || status === "Completed" || !deadline) {
    return { bg: "var(--color-grey-25)", border: "var(--color-grey-200)" };
  }

  const daysRemaining = (deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24);

  if (daysRemaining <= 7) return { bg: "var(--color-red-50)", border: "var(--color-red-200)" };
  if (daysRemaining <= 30) return { bg: "var(--color-amber-orange-25)", border: "var(--color-amber-orange-200)" };
  return { bg: "var(--color-mint-green-100)", border: "var(--color-mint-green-300)" };
};
